import { defineComponent as _defineComponent } from 'vue'
import {
  computed, getCurrentInstance, onBeforeUnmount, onMounted, ref,
  watch,
} from 'vue';
import { useSettingsBreadcrumbs } from '@/mixins/settings-breadcrumbs';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import Service from '@/model/Service';
import i18n from '@/plugins/i18n';
import localized from '@/plugins/vue-localized-formatter/src/localized';
import Sortable from 'sortablejs';
import { cloneModel } from '@/model/model-utils';
import { performDispatchAction, performSaveAction } from '@/services/vue-utils';
import { loadStoreModule, unloadStoreModule } from '@/services/module-loader';
import ServiceManagerStore from '@/store/service-manager-store';
import ReservationService from '@/grpc-api/model/reservation-service';
import store from '@/store/store';
import { useRouter } from 'vue-router/composables';
import { useVuetify } from '../helpers/vue2-helpers';
import showConfirmationDialog from '../dialogs/confirmation-dialog';


export default /*#__PURE__*/_defineComponent({
  __name: 'ServiceManager',
  setup(__props) {

const vuetify = useVuetify();
const router = useRouter();

const headers = computed(
  () => [
    {
      text: '', width: '32', value: 'id', align: 'center',
    },
    {
      text: i18n.t('label.service_manager_image'),
      width: '50',
      value: 'imageUrl',
      align: 'center',
    },
    {
      text: i18n.t('label.color'), width: '24', value: 'color', align: 'center', cellClass: 'mx-1',
    },
    {
      text: i18n.t('label.service_title'), value: 'title', align: 'start', width: '100%',
    },
    ...vuetify.breakpoint.lgAndUp
      ? [{
        text: i18n.t('label.weekdays'), value: 'weekDays', align: 'start', cellClass: 'dt-cell-stretch',
      }] : [],

    ...vuetify.breakpoint.mdAndUp
      ? [{
        text: i18n.t('label.service_time_from'), value: 'localStartDate', align: 'center', width: '32',
      },
      {
        text: i18n.t('label.service_time_until'), value: 'localEndDate', align: 'center', width: '32',
      }] : [],
    {
      text: i18n.t('label.active'), width: '32', value: 'isActive', align: 'start', sortable: true,
    },
    { value: 'actions', width: '32' },
  ],
);

const colorForServiceStatus = (s: ReservationService) => {
  if (!s.isActive) return 'gray';
  if (s.tabs.length > 0 && s.tabs.every((t) => !t.usingWeekdaysAndTimes)) { return 'red'; }
  return 'success';
};
const labelForServiceStatus = (s: ReservationService) => {
  if (!s.isActive) return i18n.t('label.inactive');
  if (s.tabs.length > 0 && s.tabs.every((t) => !t.usingWeekdaysAndTimes)) { return i18n.t('label.offline'); }
  return i18n.t('label.active');
};

const services = ref([] as ReservationService[]);

const edit = (s: Service | undefined = undefined) => router.push(`/settings/service-manager/${s?.id || 'new'}`);

const showInactive = ref(false);

const reset = async () => {
  const entities = store.getters.serviceManagertServices as ReservationService[];
  services.value = entities.map((s) => cloneModel(s)).filter((s) => (showInactive.value || s.isActive));
};

const load = async () => {
  if (!store.getters.isServiceManagerLoaded) {
    await performDispatchAction('loadServiceManager', {}, { streamLoad: true }) as ReservationService[] | null;
  }
  reset();
};

watch(showInactive, () => reset());

const changeOrder = async (event: Sortable.SortableEvent) => {
  if (event.oldIndex === undefined || event.newIndex === undefined) return;
  const movedItem = services.value.splice(event.oldIndex, 1)[0];
  services.value.splice(event.newIndex, 0, movedItem);

  const saveItem = cloneModel(movedItem);
  if (event.newIndex === 0)saveItem.order = 0; else saveItem.order = services.value[event.newIndex - 1].order;
  await performSaveAction(
    undefined,
    async () => store.dispatch('serviceManagerReorderServices', { services: [saveItem] }),
  );
  reset();
};

const duplicateService = (s: Service) => {
  router.push(`/settings/service-manager/${s.id}?action=copy`);
};

const deleteService = async (svc: ReservationService) => {
  const s = cloneModel(svc);
  s.isDeleted = true;
  const ok = await performSaveAction(
    undefined,
    async () => store.dispatch('serviceManagerUpdateService', { service: s }),
  );
  reset();
};

const deleteConfirmation = (svc: ReservationService) => {
  showConfirmationDialog(
    i18n.tc('title.service_manager'),
    i18n.tc('message.confirm_delete_service'),
    { title: i18n.tc('button.yes'), action: () => deleteService(svc) },
    { title: i18n.tc('button.cancel') },
  );
};

onMounted(() => {
  loadStoreModule(getCurrentInstance()?.proxy!, 'serviceManager', ServiceManagerStore, async () => { load(); });
});

onBeforeUnmount(() => {
  console.log('serviceManager onBeforeUnmount');

  unloadStoreModule(
    getCurrentInstance()?.proxy!,
    'serviceManager',
    async () => { store.dispatch('resetServiceManager'); },
  );
});


return { __sfc: true,vuetify, router, headers, colorForServiceStatus, labelForServiceStatus, services, edit, showInactive, reset, load, changeOrder, duplicateService, deleteService, deleteConfirmation, useSettingsBreadcrumbs, SettingsForm, localized }
}

})